import { useState, useCallback, useEffect, useRef } from 'react';
import validators from '@airasia-common/libraries/validation/Validator';
import {
  validateMissingFields,
  isRequiredError,
  validateErrors,
  scrollToElement,
} from './useFormUtils';

const getInitialTouched = initialValues => {
  return Object.keys(initialValues).reduce((value, key) => {
    return {
      ...value,
      [key]: !!initialValues[key],
    };
  }, {});
};

const useStateWithCallback = (initalState, callback) => {
  const [state, setState] = useState(initalState);
  useEffect(() => callback(state), [state, callback]);
  return [state, setState];
};
const ValidateOnEvent = {
  ValidateOnChange: 'validateOnChange',
  ValidateOnBlur: 'validateOnBlur',
  ValidateOnFocus: 'validateOnFocus',
};

const useForm = (
  initialValues,
  validationSchema = {},
  {
    onBlur = () => Function.prototype,
    onSubmit = () => Function.prototype,
    onChange = () => Function.prototype,
    onFocus = () => Function.prototype,
    onClick = () => Function.prototype,
    gtmTrackEmptyFields = () => Function.prototype,
    scrollToIdIfError,
  }
) => {
  const emptyFieldCounter = useRef(0);
  const [values, setValues] = useState(initialValues || {});
  const [touched, setTouched] = useState(getInitialTouched(initialValues));
  const [hasMissingField, setHasMissingField] = useState(false);
  const [targetEvent, setEvent] = useState({});

  const setErrorsCallback = useCallback(errors => {
    setHasMissingField(validateMissingFields(errors));
  }, []);

  const [errors, setErrors] = useStateWithCallback({}, setErrorsCallback);

  useEffect(() => {
    const { name, value, eventName, event } = targetEvent;
    /*if (hasMissingField !== validateMissingFields(errors)) {
      return;
    }*/
    switch (eventName) {
      case ValidateOnEvent.ValidateOnChange:
        onChange(name, value, event);
        break;
      case ValidateOnEvent.ValidateOnBlur:
        onBlur(name, value, event);
        break;
      case ValidateOnEvent.ValidateOnFocus:
        onFocus(name, event);
        break;
      default:
        break;
    }
    return () => {
      const { eventName } = targetEvent;
      if (eventName) {
        setEvent({});
      }
    };
    // eslint-disable-next-line
  }, [targetEvent]);

  useEffect(() => {
    let errorFieldName = '';
    const newEmptyFieldsCount = Object.keys(errors).reduce(
      (errorCount, fieldName) => {
        if (errors[fieldName].error && errors[fieldName].type === 'required') {
          errorFieldName = fieldName;
          return ++errorCount;
        }
        return errorCount;
      },
      0
    );

    if (newEmptyFieldsCount !== emptyFieldCounter.current) {
      emptyFieldCounter.current = newEmptyFieldsCount;
      gtmTrackEmptyFields({
        fieldName: errorFieldName,
        errorCount: newEmptyFieldsCount,
      });
    }
    // eslint-disable-next-line
  }, [errors]);

  const handleSubmit = callbackSubmitFunc => {
    touchAllFields();
    const errors = validateAllFields();
    setErrors(errors);

    // If there is no errors, submit value else abort
    if (!validateErrors(errors)) {
      const submitFunc = callbackSubmitFunc || onSubmit;
      submitFunc(values);
      return values;
    } else {
      scrollToIdIfError && scrollToElement(scrollToIdIfError);
    }
  };

  const handleChange = name => (value, event) => {
    runFieldLevelValidations(ValidateOnEvent.ValidateOnChange)(name, value);
    setValues(previousValues => ({ ...previousValues, [name]: value }));
    setEvent(previousTargetEvent => ({
      ...previousTargetEvent,
      name,
      value,
      event,
      eventName: ValidateOnEvent.ValidateOnChange,
    }));
  };

  const handleBlur = name => event => {
    runFieldLevelValidations(ValidateOnEvent.ValidateOnBlur)(
      name,
      values[name]
    );
    setTouched(previousTouched => ({ ...previousTouched, [name]: true }));
    setEvent(previousTargetEvent => ({
      ...previousTargetEvent,
      name,
      value: values[name],
      event,
      eventName: ValidateOnEvent.ValidateOnBlur,
    }));
  };

  const handleFocus = name => event => {
    setTouched(previousTouched => ({ ...previousTouched, [name]: true }));
    setEvent(previousTargetEvent => ({
      ...previousTargetEvent,
      name,
      event,
      eventName: ValidateOnEvent.ValidateOnFocus,
    }));
  };

  const handleClick = name => event => {
    if (typeof onClick === 'function') {
      onClick(name, event);
    }
  };

  const validatorOnEvent = (name, rule, validatorEvent) => {
    const { [validatorEvent]: validatorFlag = true } = rule;
    return (
      validatorFlag ||
      (touched[name] &&
        errors[name] &&
        errors[name].error &&
        !isRequiredError(errors[name]))
    );
  };

  const validateByFieldName = (name, value = '', validatorEvent) => {
    let error = {};
    const fieldValidatorSchema = validationSchema[name];
    const skipValidations =
      fieldValidatorSchema?.optional && value.trim() === '';

    if (skipValidations) {
      return { [name]: { error: false } };
    }

    fieldValidatorSchema &&
      Object.keys(fieldValidatorSchema).some(key => {
        const validator = validators[key];

        // ignore invalid validation props
        if (!validator) return false;

        const rule = fieldValidatorSchema[key];
        if (!validatorOnEvent(name, rule, validatorEvent)) {
          return false;
        }

        const isValid = validator(value, rule);

        error = {
          ...error,
          [name]: {
            error: !isValid,
            errorMsg: rule.message,
            type: rule.type || key,
          },
        };

        return !isValid;
      });

    return error;
  };

  const validateAllFields = () => {
    return Object.keys(validationSchema).reduce((value, key) => {
      const error = validateByFieldName(key, values[key]);
      return {
        ...value,
        ...error,
      };
    }, {});
  };

  const runFieldLevelValidations = validatorEvent => (name, value) => {
    const error = validateByFieldName(name, value, validatorEvent);
    setErrors(previousErrors => ({ ...previousErrors, ...error }));
  };

  const touchAllFields = () => {
    const touched = Object.keys(values).reduce((value, key) => {
      return {
        ...value,
        [key]: true,
      };
    }, {});
    setTouched(touched);
  };

  const setFieldValue = useCallback(
    (name, value, untouched = false, option = {}) => {
      setValues(previousValues => ({
        ...previousValues,
        [name]: value,
      }));
      if (untouched === true) {
        setTouched(prevTouched => ({
          ...prevTouched,
          [name]: false,
        }));
        setErrors(prevErrors => ({
          ...prevErrors,
          [name]: false,
        }));
      }
      Object.keys(option).forEach(key => {
        switch (key) {
          case 'touched':
            setTouched(prevTouched => ({
              ...prevTouched,
              [name]: option[key],
            }));
            break;
          case 'error':
            setErrors(previousValues => ({
              ...previousValues,
              [name]: option[key],
            }));
            break;
          default:
            break;
        }
      });
    },
    // eslint-disable-next-line
    []
  );

  const setErrorMsg = (name, errorMsg) => {
    setErrors(previousValues => ({
      ...previousValues,
      [name]: errorMsg,
    }));
  };

  const resetForm = useCallback(
    (initial = {}) => {
      setValues(initial);
      setErrors({});
      setTouched(getInitialTouched(initial));
    },
    // eslint-disable-next-line
    [setValues]
  );

  // Shorthand to pass appropriate props to FieldValidator component
  const createValidationProps = name => ({
    name,
    touched,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onClick: handleClick,
    values,
    errors,
  });

  return {
    values,
    errors,
    touched,
    hasMissingField,
    handleChange,
    handleBlur,
    handleFocus,
    handleClick,
    handleSubmit,
    createValidationProps,
    validateAllFields,
    validateByFieldName,
    setFieldValue,
    setErrorMsg,
    resetForm,
  };
};

export default useForm;
